import React from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { getPartnerDocsPublicHtml } from "app/services/api/apiDocs";

const SS_KEY = "partnerDocsCreds";

export default function PartnerDocsPublicPage() {
  const { t } = useTranslation();

  const [user, setUser] = React.useState("");
  const [pass, setPass] = React.useState("");
  const [showPass, setShowPass] = React.useState(false);
  const [html, setHtml] = React.useState("");
  const [authed, setAuthed] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // načítanie dokumentácie pre dané údaje + jazyk
  const load = React.useCallback(async (u, p, l) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPartnerDocsPublicHtml(u, p, l);
      setHtml(res);
      setAuthed(true);
      try {
        sessionStorage.setItem(SS_KEY, btoa(unescape(encodeURIComponent(`${u}:${p}`))));
      } catch (_e) {}
      return true;
    } catch (e) {
      const status = e?.response?.status;
      setAuthed(false);
      setHtml("");
      setError(
        status === 401
          ? t("pages.partnerDocs.wrongCreds", {
              defaultValue: "Nesprávne meno alebo heslo.",
            })
          : t("pages.partnerDocs.loadError", {
              defaultValue: "Dokumentáciu sa nepodarilo načítať.",
            })
      );
      return false;
    } finally {
      setLoading(false);
    }
  }, [t]);

  // obnova relácie z sessionStorage (prežije prepnutie jazyka / refresh počas session)
  React.useEffect(() => {
    let creds = null;
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      if (raw) creds = decodeURIComponent(escape(atob(raw)));
    } catch (_e) {}
    if (creds) {
      const sep = creds.indexOf(":");
      const u = creds.slice(0, sep);
      const p = creds.slice(sep + 1);
      setUser(u);
      setPass(p);
      load(u, p, "sk");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user || !pass) return;
    load(user, pass, "sk");
  };

  const title = t("pages.partnerDocs.title", {
    defaultValue: "Partnerská API dokumentácia",
  });

  // --- prihlásený: čisté docs na celú výšku (bez vrchného headera) ---
  if (authed) {
    return (
      <Box sx={{ position: "relative", height: "100vh", bgcolor: "#fff" }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <iframe
          title={title}
          srcDoc={html}
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      </Box>
    );
  }

  // --- neprihlásený: login formulár ---
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        bgcolor: "background.default",
      }}
    >
      <Card variant="outlined" sx={{ width: "100%", maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 0.5, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("pages.partnerDocs.subtitle", {
              defaultValue: "Zadajte prihlasovacie údaje pre prístup k dokumentácii.",
            })}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label={t("pages.partnerDocs.user", { defaultValue: "Meno" })}
                value={user}
                onChange={(e) => setUser(e.target.value)}
                autoFocus
                fullWidth
                autoComplete="username"
              />
              <TextField
                label={t("pages.partnerDocs.password", { defaultValue: "Heslo" })}
                type={showPass ? "text" : "password"}
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                fullWidth
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPass((s) => !s)}
                        edge="end"
                        size="small"
                      >
                        {showPass ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || !user || !pass}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {t("pages.partnerDocs.submit", { defaultValue: "Zobraziť dokumentáciu" })}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
